import { Suspense, lazy } from 'react';

import { AiChatContextProvider } from '@cloudquery/ai-chat';
import { useFeatureFlags, useBaseAuth } from '@cloudquery/base-users';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import { generateUrl, Loader, NotFound, paths, SplashScreen } from 'shared/base';

import { PlatformPermissions } from '@/constants/platformPermissions';
import { useAllowedPlatformFeatures } from '@/hooks/useAllowedPlatformFeatures';

import { useHasTeamsPermissions } from '@/hooks/useHasTeamsPermissions';

import TeamReportsIndexPage from './(authenticated)/teams/[team]/reports';
import UserSettingsMfaPage from './(authenticated)/user-settings/mfa';

const DashboardLayout = lazy(() =>
  import('@/layouts/dasboard').then((module) => ({ default: module.DashboardLayout })),
);

const AdminSettingsPage = lazy(() => import('./(authenticated)/admin/settings'));
const AdminUsersPage = lazy(() => import('./(authenticated)/admin/users'));
const AdminDataPermissionsPage = lazy(() => import('./(authenticated)/admin/data-permissions'));
const AdminDataPermissionsCreatePage = lazy(
  () => import('./(authenticated)/admin/data-permissions/create'),
);
const AdminDataPermissionsEditPage = lazy(
  () => import('./(authenticated)/admin/data-permissions/edit'),
);
const AdminRolesPage = lazy(() => import('./(authenticated)/admin/roles'));
const AdminRolesCreatePage = lazy(() => import('./(authenticated)/admin/roles/create'));
const AdminRolesEditPage = lazy(() => import('./(authenticated)/admin/roles/edit'));
const AdminSSOPage = lazy(() => import('./(authenticated)/admin/sso'));
const AdminCustomColumnsPage = lazy(() => import('./(authenticated)/admin/custom-columns'));
const AdminAuditLogsPage = lazy(() => import('./(authenticated)/admin/audit-logs'));
const AdminApiKeysPage = lazy(() => import('./(authenticated)/admin/api-keys'));
const AdminUpdatesPage = lazy(() => import('./(authenticated)/admin/updates'));
const AdminFeatureFlagsPage = lazy(() => import('./(authenticated)/admin/feature-flags'));

const UserSettingsPage = lazy(() => import('./(authenticated)/user-settings'));

const CreateTeamPage = lazy(() => import('./(authenticated)/create-team'));

const TeamPage = lazy(() => import('./(authenticated)/teams/[team]'));

const TeamPoliciesCreatePage = lazy(() => import('./(authenticated)/teams/[team]/policies/create'));
const TeamPoliciesPage = lazy(() => import('./(authenticated)/teams/[team]/policies/index'));
const TeamPoliciesDetailPage = lazy(() => import('./(authenticated)/teams/[team]/policies/[id]'));

const ReportPage = lazy(() => import('./(authenticated)/teams/[team]/reports/[report]/index'));
const UsagePage = lazy(() => import('./(authenticated)/teams/[team]/usage'));

const TeamAssetsInventoryPage = lazy(
  () => import('./(authenticated)/teams/[team]/asset-inventory'),
);
const TeamSqlConsolePage = lazy(() => import('./(authenticated)/teams/[team]/sql-console'));
const TeamSyncsPage = lazy(() => import('./(authenticated)/teams/[team]/syncs'));
const TeamSyncsUpdatePage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/[name]/update'),
);
const TeamSyncsCreatePage = lazy(() => import('./(authenticated)/teams/[team]/syncs/create'));
const TeamSyncRunsPage = lazy(() => import('./(authenticated)/teams/[team]/syncs/[name]/runs'));

const TeamSyncSourcesPage = lazy(() => import('./(authenticated)/teams/[team]/syncs/sources'));
const TeamSyncSourceCreatePage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/sources/create'),
);
const TeamSyncSourceUpdatePage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/sources/[name]/update'),
);
const TeamSyncSourceSyncsPage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/sources/[name]/syncs'),
);

const TeamSyncDestinationsPage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/destinations'),
);
const TeamSyncDestinationCreatePage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/destinations/create'),
);
const TeamSyncDestinationUpdatePage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/destinations/[name]/update'),
);
const TeamSyncDestinationSyncsPage = lazy(
  () => import('./(authenticated)/teams/[team]/syncs/destinations/[name]/syncs'),
);
const AdminNotificationDestinationsPage = lazy(
  () => import('./(authenticated)/admin/notification-destinations'),
);

const AuthLayout = lazy(() => import('./auth/layout'));
const AuthLoginPage = lazy(() => import('./auth/login'));

const OnboardingPage = lazy(() => import('./onboarding'));
const OnboardingLayout = lazy(() => import('./onboarding/layout'));

export function PageRoutes() {
  const { currentMembership } = useBaseAuth();
  const { customColumnsAllowed, dataPermissionsAndRolesEnabled, policiesAllowed } =
    useAllowedPlatformFeatures();

  const canEditDataPipelines = useHasTeamsPermissions([PlatformPermissions.ADMIN_WRITE]);
  const isAdmin = useHasTeamsPermissions([PlatformPermissions.ADMIN_READ]);

  const { allowedEditingFeatureFlags } = useFeatureFlags();

  const legacyPathsRedirect = [
    {
      applicable: isAdmin,
      from: '/teams/[team]/settings',
      to: paths.platform.settings.organization.settings,
    },
    {
      applicable: isAdmin,
      from: '/teams/[team]/settings/members',
      to: paths.platform.settings.organization.users,
    },
    {
      from: '/teams/[team]/settings/api-keys',
      to: paths.platform.settings.organization.apiKeys,
    },
    {
      applicable: customColumnsAllowed,
      from: '/teams/[team]/settings/custom-columns',
      to: paths.platform.settings.organization.customColumns,
    },
  ];

  const indexElement = currentMembership ? (
    <Navigate
      to={generateUrl({
        pathname: paths.platform.team.index,
        query: { team: currentMembership.team.name },
      })}
    />
  ) : (
    <Navigate to={paths.platform.auth.login} />
  );

  const notFoundElement = (
    <NotFound
      returnTo={generateUrl({
        pathname: paths.platform.index,
      })}
    />
  );

  return (
    <Suspense fallback={<SplashScreen />}>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route
            element={<AuthLoginPage />}
            path={parsePathToReactRouterPath(paths.platform.auth.login)}
          />
        </Route>

        <Route element={<OnboardingLayout />}>
          <Route element={<OnboardingPage />} path={parsePathToReactRouterPath(paths.onboarding)} />
        </Route>

        <Route element={indexElement} path="/" />

        <Route element={<DashboardLayout />}>
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            }
          >
            {isAdmin && (
              <>
                <Route
                  element={<AdminSettingsPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.settings)}
                />
                <Route
                  element={<AdminUsersPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.users)}
                />
                {dataPermissionsAndRolesEnabled && (
                  <>
                    <Route
                      element={
                        <AiChatContextProvider>
                          <AdminDataPermissionsPage />
                        </AiChatContextProvider>
                      }
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.dataPermissions.index,
                      )}
                    />
                    <Route
                      element={<AdminDataPermissionsCreatePage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.dataPermissions.create,
                      )}
                    />
                    <Route
                      element={<AdminDataPermissionsEditPage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.dataPermissions.edit,
                      )}
                    />
                    <Route
                      element={<AdminRolesPage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.roles.index,
                      )}
                    />
                    <Route
                      element={<AdminRolesCreatePage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.roles.create,
                      )}
                    />
                    <Route
                      element={<AdminRolesEditPage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.settings.organization.roles.edit,
                      )}
                    />
                  </>
                )}
                <Route
                  element={<AdminSSOPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.sso)}
                />
                <Route
                  element={<AdminApiKeysPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.apiKeys)}
                />
                {customColumnsAllowed && (
                  <Route
                    element={<AdminCustomColumnsPage />}
                    path={parsePathToReactRouterPath(
                      paths.platform.settings.organization.customColumns,
                    )}
                  />
                )}
                <Route
                  element={<AdminNotificationDestinationsPage />}
                  path={parsePathToReactRouterPath(
                    paths.platform.settings.organization.notificationDestinations,
                  )}
                />
                <Route
                  element={<AdminAuditLogsPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.auditLogs)}
                />
                <Route
                  element={<AdminUpdatesPage />}
                  path={parsePathToReactRouterPath(paths.platform.settings.organization.updates)}
                />
                {allowedEditingFeatureFlags && (
                  <Route
                    element={<AdminFeatureFlagsPage />}
                    path={parsePathToReactRouterPath(
                      paths.platform.settings.organization.featureFlags,
                    )}
                  />
                )}
              </>
            )}

            <Route
              element={<UserSettingsPage />}
              path={parsePathToReactRouterPath(paths.platform.settings.account.index)}
            />
            <Route
              element={<UserSettingsMfaPage />}
              path={parsePathToReactRouterPath(paths.platform.settings.account.mfa)}
            />

            <Route
              element={<CreateTeamPage />}
              path={parsePathToReactRouterPath(paths.platform.createTeam)}
            />

            <Route
              element={<TeamPage />}
              path={parsePathToReactRouterPath(paths.platform.team.index)}
            />

            {policiesAllowed && (
              <>
                <Route
                  element={<TeamPoliciesPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.policies.index)}
                />
                <Route
                  element={<TeamPoliciesCreatePage />}
                  path={parsePathToReactRouterPath(paths.platform.team.policies.create)}
                />
                <Route
                  element={<TeamPoliciesDetailPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.policies.detail)}
                />
              </>
            )}

            <Route
              element={<TeamAssetsInventoryPage />}
              path={parsePathToReactRouterPath(paths.platform.team.assetInventory.index)}
            />
            <Route
              element={
                <AiChatContextProvider>
                  <TeamSqlConsolePage />
                </AiChatContextProvider>
              }
              path={parsePathToReactRouterPath(paths.platform.team.sqlConsole.index)}
            />
            <Route
              element={<TeamReportsIndexPage />}
              path={parsePathToReactRouterPath(paths.platform.team.reports.index)}
            />
            <Route
              element={<ReportPage />}
              path={parsePathToReactRouterPath(paths.platform.team.reports.report)}
            />

            {isAdmin && (
              <>
                {/* Begin Syncs */}
                <Route
                  element={<TeamSyncsPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.index)}
                />
                {canEditDataPipelines && (
                  <>
                    <Route
                      element={<TeamSyncsUpdatePage />}
                      path={parsePathToReactRouterPath(paths.platform.team.syncs.update)}
                    />
                    <Route
                      element={<TeamSyncsCreatePage />}
                      path={parsePathToReactRouterPath(paths.platform.team.syncs.create)}
                    />
                  </>
                )}
                <Route
                  element={<TeamSyncRunsPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.runs.index)}
                />

                {/* End Syncs */}

                {/* Begin Sync Sources */}
                <Route
                  element={<TeamSyncSourcesPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.sources.index)}
                />
                {canEditDataPipelines && (
                  <>
                    <Route
                      element={<TeamSyncSourceCreatePage />}
                      path={parsePathToReactRouterPath(paths.platform.team.syncs.sources.create)}
                    />
                    <Route
                      element={<TeamSyncSourceUpdatePage />}
                      path={parsePathToReactRouterPath(paths.platform.team.syncs.sources.update)}
                    />
                  </>
                )}
                <Route
                  element={<TeamSyncSourceSyncsPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.sources.syncs)}
                />
                {/* End Sync Sources */}

                {/* Begin Sync Destinations */}
                <Route
                  element={<TeamSyncDestinationsPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.destinations.index)}
                />
                {canEditDataPipelines && (
                  <>
                    <Route
                      element={<TeamSyncDestinationCreatePage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.team.syncs.destinations.create,
                      )}
                    />
                    <Route
                      element={<TeamSyncDestinationUpdatePage />}
                      path={parsePathToReactRouterPath(
                        paths.platform.team.syncs.destinations.update,
                      )}
                    />
                  </>
                )}
                <Route
                  element={<TeamSyncDestinationSyncsPage />}
                  path={parsePathToReactRouterPath(paths.platform.team.syncs.destinations.syncs)}
                />
                {/* End Sync Destinations */}
              </>
            )}

            {isAdmin && (
              <Route
                element={<UsagePage />}
                path={parsePathToReactRouterPath(paths.platform.team.usage.index)}
              />
            )}

            <Route element={notFoundElement} path="*" />
          </Route>
        </Route>
        {/* Redirect legacy paths to new paths */}
        {legacyPathsRedirect.map(({ applicable, from, to }) =>
          applicable ? (
            <Route
              key={from}
              element={<Navigate replace={true} to={parsePathToReactRouterPath(to)} />}
              path={parsePathToReactRouterPath(from)}
            />
          ) : null,
        )}
        {/* End Redirect legacy paths to new paths */}
      </Routes>
    </Suspense>
  );
}

function parsePathToReactRouterPath(path: string) {
  return path.replaceAll(/\[([^\]]+)\]/g, ':$1');
}
